/** Angular imports */
import { isPlatformBrowser } from '@angular/common';
import {
    AfterViewInit,
    Component,
    computed,
    DestroyRef,
    HostListener,
    inject,
    OnDestroy,
    OnInit,
    PLATFORM_ID,
    signal,
    TemplateRef,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { catchError, EMPTY } from 'rxjs';

/** Component imports */
import { Button, DateRangePicker, Dropdown, DropdownOption } from '@ntv360/component-pantry';
import {
    ConfirmationModalComponent,
    FilterDropdownComponent,
    FilterOption,
    NtvTableCellDirective,
    StatsCardComponent,
    StatsData,
    TableComponent,
    TableToolbarButton,
    TableToolbarComponent,
} from '@shared';

/** Core imports */
import { ApiLicenseStats, ExportService } from '@core';
import { AuthService } from '@core/services/auth/auth.service';
import { LicensesService } from '@core/services/licenses/licenses.service';
import { TimezoneService } from '@core/services/timezone/timezone.service';
import { SubNavbarService } from '@layouts/main-layout/components/sub-navbar/sub-navbar.service';
import { GenerateLicenseModalComponent } from './components/generate-license-modal/generate-license-modal.component';

/** Local imports */
import { SVG_ICONS } from '@shared/constants';
import { CopyDirective } from '@shared/directives';
import { FilterOperator, QueryFilter } from '@shared/interfaces/filter-operator.interface';
import {
    formatDateLong,
    formatDateOnly,
    formatTime,
    Logger,
    mapTimezonesForDropdown,
    withLoading,
} from '@shared/utils';
import { FILTER_BY_TREE, LICENSE_COLUMNS, LICENSE_STATUS_COLOR_MAP } from './constants';
import { FilterNode, LicenseFilterState, UILicense } from './interfaces';

@Component({
    selector: 'app-licenses',
    standalone: true,
    imports: [
        StatsCardComponent,
        Button,
        Dropdown,
        FilterDropdownComponent,
        TableToolbarComponent,
        DateRangePicker,
        TableComponent,
        TableComponent,
        NtvTableCellDirective,
        ConfirmationModalComponent,
        GenerateLicenseModalComponent,
        CopyDirective,
    ],
    templateUrl: './licenses.component.html',
    styleUrl: './licenses.component.scss',
})
export class LicensesComponent implements OnInit, AfterViewInit, OnDestroy {
    protected subNavBarContent = viewChild<TemplateRef<any>>('subNavBarContent');
    protected subNavbarService = inject(SubNavbarService);

    /**
     * Injected export service for CSV export.
     */
    private exportService = inject(ExportService);

    /**
     * Injected licenses service for license-related API operations.
     */
    private licensesService = inject(LicensesService);

    /**
     * Injected timezone service for fetching available timezones.
     */
    private timezoneService = inject(TimezoneService);

    /**
     * Injected DestroyRef for automated cleanup of RxJS subscriptions.
     */
    private destroyRef = inject(DestroyRef);

    /**
     * Injected auth service for role-based UI checks.
     */
    private authService = inject(AuthService);

    /**
     * Whether the current user has a Dealer role (hides admin-only actions like Generate License).
     */
    public isDealer = computed(() => {
        const traits = this.authService.userTraits() as { data?: { roleName?: string } } | null;
        const role = traits?.data?.roleName;
        return role === 'Dealer' || role === 'Dealer Admin';
    });

    /**
     * Internal signal storing the raw license data fetched from the API.
     */
    private licenseData = signal<UILicense[]>([]);

    public totalCount = signal<number>(0);

    protected page = signal<number>(1);

    protected limit = signal<number>(10);

    private isLoadMore = false;

    public isShowMoreLoading = signal<boolean>(false);

    /**
     * Signal to manually trigger a refresh of the license data stream.
     * Incrementing this value will cause the reactive stream to refetch data.
     */
    private refreshTrigger = signal<number>(0);
    /**
     * Flag indicating if the code is executing in a browser environment (vs SSR).
     * Used for platform-specific logic.
     */
    public isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

    /**
     * Reference to the SVG icon constant map.
     */
    public icons = SVG_ICONS;

    /**
     * Relative path to the SVG sprite asset.
     */
    public spritePath = 'assets/icons/icons-sprite.svg';

    /**
     * Computed signal for statistics card data.
     */
    public statsData = signal<StatsData[]>([]);
    public isStatsLoading = signal(true);

    /**
     * Configuration for table columns based on LICENSE_COLUMNS.
     */
    public licenseColumns = LICENSE_COLUMNS;

    /**
     * Signal for the currently selected date range filter.
     */
    public selectedDateRange = signal<{ start: Date; end: Date } | null>(null);

    /**
     * Signal for the currently selected timezone ID filter.
     */
    public selectedTimezoneId = signal<number | null>(null);

    /**
     * Signal for the current search query string.
     */
    public searchTerm = signal<string>('');

    /**
     * Signal tracking the collection of rows currently selected in the table.
     */
    public selectedRows = signal<UILicense[]>([]);

    /**
     * Signal for tracking the global loading state of the license data.
     */
    public isLoading = signal<boolean>(true);

    /**
     * Data structure for the hierarchical "Filter by" dropdown.
     */
    public filterTree = FILTER_BY_TREE;

    /**
     * Signal mapping filter node IDs to their checked status.
     */
    public filterCheckedMap = signal<Record<string, boolean>>({});

    /**
     * Signal mapping parent filter node IDs to their expanded/collapsed visibility status.
     */
    public filterExpandedMap = signal<Record<string, boolean>>({});

    /**
     * Signal tracking if the "Filter by" dropdown menu is currently open.
     */
    public isFilterOpen = signal(false);

    /**
     * Signal for showing the bulk delete confirmation modal.
     */
    public showDeleteModal = signal(false);

    /**
     * Signal for showing the single license delete confirmation modal.
     */
    public showSingleDeleteModal = signal(false);

    /**
     * The license currently targeted for single deletion.
     */
    public licenseToDelete = signal<UILicense | null>(null);

    /**
     * Signal for showing the bulk deletion success notification modal.
     */
    public showSuccessModal = signal(false);
    public showSingleDeleteSuccessModal = signal(false);
    public showGenerateModal = signal(false);
    public showGenerateSuccessModal = signal(false);

    /**
     * Signal to hide the elements for now.
     * This is a temporary solution to hide the elemtents for tech magic.
     */
    public isHiddenForNow = signal(true);

    /**
     * Computed signal representing the licenses to be displayed in the table.
     * Currently a direct pass-through of licenseData as server-side filtering is preferred.
     */
    public filteredLicenseData = computed<UILicense[]>(() => {
        const licenseData = this.licenseData();
        if (!licenseData) return [];
        const data = licenseData.map((license) => {
            const statusProp = license.displayStatus === 'ON' ? 'online' : 'offline';
            const statusColor =
                LICENSE_STATUS_COLOR_MAP[statusProp as keyof typeof LICENSE_STATUS_COLOR_MAP];
            return {
                ...license,
                statusColor,
            };
        });
        console.log('filtered data', data);
        return data;
    });

    /**
     * Computed FilterOption tree for the shared `app-filter-dropdown` component.
     * Mirrors {@link filterTree} merged with current checked/expanded signal state.
     */
    public filterTreeOptions = computed<FilterOption[]>(() => {
        const checkedMap = this.filterCheckedMap();
        const expandedMap = this.filterExpandedMap();
        const map = (nodes: FilterNode[]): FilterOption[] =>
            nodes.map((n) => ({
                id: n.id,
                label: n.label,
                isRadio: n.isRadio,
                checked: !!checkedMap[n.id],
                expanded: expandedMap[n.id] ?? false,
                children: n.children?.length ? map(n.children) : undefined,
            }));
        return map(this.filterTree);
    });

    /**
     * Finds a specific {@link FilterNode} by its unique ID within the filter tree.
     *
     * @param {string} id - The unique identifier of the node to locate.
     * @param {FilterNode[]} nodes - The array of nodes to search through (defaults to the root tree).
     * @returns {FilterNode | null} The found node object or null if not located.
     * @private
     */
    private findFilterNodeById(
        id: string,
        nodes: FilterNode[] = this.filterTree,
    ): FilterNode | null {
        for (const n of nodes) {
            if (n.id === id) return n;
            if (n.children?.length) {
                const found = this.findFilterNodeById(id, n.children);
                if (found) return found;
            }
        }
        return null;
    }

    /**
     * Handler for checkbox changes emitted by the shared filter dropdown tree.
     * Coordinates the mapping from UI `FilterOption` back to backend `FilterNode` logic.
     *
     * @param {Object} event - The checkbox change event data.
     * @param {FilterOption} event.node - The UI node that was toggled.
     * @param {boolean} event.checked - The new checked state.
     * @returns {void}
     */
    public onTreeCheckedChange(event: { node: FilterOption; checked: boolean }): void {
        const original = event.node.id ? this.findFilterNodeById(event.node.id) : null;
        if (original) {
            this.onFilterCheckboxChange(original, event.checked);
        }
    }

    /**
     * Handler for expand/collapse toggle events in the filter tree.
     *
     * @param {FilterOption} node - The UI node to expand or collapse.
     * @returns {void}
     */
    public onTreeExpandToggle(node: FilterOption): void {
        if (node.id) {
            this.toggleFilterGroup(node.id);
        }
    }

    ngOnInit(): void {
        this.getLicensesStats();
    }

    ngAfterViewInit(): void {
        if (this.subNavBarContent()) {
            this.subNavbarService.setContent(this.subNavBarContent()!);
        }
    }

    ngOnDestroy(): void {
        this.subNavbarService.clearContent();
    }

    /** Opens the generate license modal when the "Generate License" button is clicked. */
    public onAddLicense(): void {
        this.showGenerateModal.set(true);
    }

    /**
     * Handles the `generate` event emitted by the generate license modal.
     * Calls the licenses API with the selected dealer and count, then on success:
     * closes the modal, shows the success confirmation, and refreshes the license list.
     *
     * @param form - The submitted form containing the dealer ID and license count.
     */
    public onLicenseGenerated(form: { dealerId: string | null; count: number | null }): void {
        if (!form.dealerId || !form.count) return;
        this.licensesService
            .generateLicenses({ licenseCount: form.count, ownerId: form.dealerId })
            .subscribe({
                next: () => {
                    this.showGenerateModal.set(false);
                    this.showGenerateSuccessModal.set(true);
                },
            });
    }

    /**
     * Closes the generate success modal and refreshes the license list so the
     * newly generated licenses appear.
     */
    public onGenerateSuccessModalClose(): void {
        this.showGenerateSuccessModal.set(false);
        this.licenseData.set([]);
        this.isLoading.set(true);
        this.refreshTrigger.update((v) => v + 1);
    }

    /**
     * Computed list of toolbar buttons configured based on the current selection state.
     * Dynamically adjusts button variants and visibility.
     */
    public toolbarButtons = computed<TableToolbarButton[]>(() => {
        const selectionCount = this.selectedRows().length;
        return [
            {
                buttonName: 'Bulk Delete',
                icon: 'delete',
                variant: selectionCount > 2 ? 'danger' : 'secondary',
            },
            {
                buttonName: 'Export',
                icon: 'export',
                variant: 'accent',
            },
        ];
    });

    /**
     * Configuration object for the timezone dropdown, including options and placeholder.
     */
    public timezoneConfig = signal({
        options: [] as { label: string; value: number }[],
        placeholder: 'Timezone',
        fullWidth: false,
    });

    /**
     * Initializes the license component and builds the default expanded state for the filter tree.
     */
    constructor() {
        this.filterExpandedMap.set(this.buildDefaultExpandedMap(FILTER_BY_TREE));
        this.initLicensesStream();
    }

    /**
     * Toggles the filter dropdown visibility.
     *
     * @param {Event} event - The click event that triggered the toggle.
     * @returns {void}
     */
    public toggleFilter(event: Event): void {
        event.stopPropagation();
        this.isFilterOpen.update((v) => !v);
    }

    /**
     * Closes the filter dropdown when clicking outside of the filter container.
     *
     * @param {MouseEvent} event - The document click event.
     * @returns {void}
     */
    @HostListener('document:click', ['$event'])
    public onDocumentClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        const isClickedInside = target.closest('.filter-container');
        if (!isClickedInside && this.isFilterOpen()) {
            this.isFilterOpen.set(false);
        }
    }

    /**
     * Builds a default state map where all parent nodes are initially collapsed.
     *
     * @param {FilterNode[]} nodes - The tree of nodes to process.
     * @returns {Record<string, boolean>} A key-value map of node IDs to collapsed state (false).
     * @private
     */
    private buildDefaultExpandedMap(nodes: FilterNode[]): Record<string, boolean> {
        const map: Record<string, boolean> = {};
        for (const node of nodes) {
            if (node.children?.length) {
                map[node.id] = false;
                Object.assign(map, this.buildDefaultExpandedMap(node.children));
            }
        }
        return map;
    }

    /**
     * Toggles the expand/collapse state of a parent filter group.
     *
     * @param {string} nodeId - The ID of the filter group node to toggle.
     * @returns {void}
     */
    public toggleFilterGroup(nodeId: string): void {
        this.filterExpandedMap.update((map) => ({
            ...map,
            [nodeId]: !map[nodeId],
        }));
    }

    /**
     * Returns whether a parent filter group is currently expanded.
     *
     * @param {string} nodeId - The ID of the node to check.
     * @returns {boolean} True if the node is expanded, false otherwise.
     */
    public isExpanded(nodeId: string): boolean {
        return this.filterExpandedMap()[nodeId] ?? false;
    }

    /**
     * Handles changes to filter checkboxes.
     * Manages mutual exclusivity for radio-style filters within the tree.
     *
     * @param {FilterNode} node - The backend filter configuration node.
     * @param {boolean} checked - The new target state of the checkbox.
     * @returns {void}
     */
    public onFilterCheckboxChange(node: FilterNode, checked: boolean): void {
        this.isLoading.set(true);
        this.isLoadMore = false;
        this.page.set(1);
        if (node.isRadio) {
            this.filterCheckedMap.update((map) => {
                const updated = { ...map };
                if (checked) {
                    this.clearRadioChecks(this.filterTree, updated);
                }
                updated[node.id] = checked;
                return updated;
            });
            this.applyFilters();
            return;
        }

        this.filterCheckedMap.update((map) => ({
            ...map,
            [node.id]: checked,
        }));

        this.applyFilters();
    }

    /**
     * Recursively clears all radio-type nodes in the checked map.
     *
     * @param {FilterNode[]} nodes - The filter nodes to check recursively.
     * @param {Record<string, boolean>} map - The checked state map to update.
     * @private
     * @returns {void}
     */
    private clearRadioChecks(nodes: FilterNode[], map: Record<string, boolean>): void {
        for (const n of nodes) {
            if (n.isRadio) {
                map[n.id] = false;
            }
            if (n.children?.length) {
                this.clearRadioChecks(n.children, map);
            }
        }
    }

    /**
     * Returns whether a filter node is checked.
     *
     * @param {FilterNode} node - The node to check.
     * @returns {boolean} True if the node is checked, false otherwise.
     */
    public isChecked(node: FilterNode): boolean {
        return !!this.filterCheckedMap()[node.id];
    }

    /**
     * Applies the current filter checkbox state to refetch licenses from the API.
     * @private
     * @returns {void}
     */
    private applyFilters(): void {
        // Data refresh is handled reactively by initLicensesStream via filterCheckedMap signal
    }

    /**
     * Translates local UI filter states into standardized API query filters.
     * Maps user-friendly categories (Assigned, Pending, Offline) to database status IDs and boolean flags.
     *
     * @returns {Record<string, FilterOperator | QueryFilter>} A standardized filter object for the API.
     * @private
     */
    private buildFiltersFromCheckboxState(): Record<string, FilterOperator | QueryFilter> {
        const filters: Record<string, FilterOperator | QueryFilter> = {};
        const map = this.filterCheckedMap();

        // Helper to safely merge nested filters
        const mergeNested = (key: string, value: FilterOperator | QueryFilter) => {
            filters[key] = { ...((filters[key] as FilterOperator | QueryFilter) || {}), ...value };
        };

        // 1. HostLocation presence (Assigned / Unassigned)
        const isAssigned = !!map['assigned'];
        const isUnassigned = !!map['unassigned'];

        if (isAssigned && !isUnassigned) {
            filters['statusId'] = { _eq: 1 };
        } else if (!isAssigned && isUnassigned) {
            filters['statusId'] = { _eq: 3 };
        }

        // 2. Assigned sub-options
        const isOnline = !!map['online'];
        const isOffline = !!map['offline'];

        // Handle Online/Offline Status
        if (isOnline && !isOffline) {
            mergeNested('status', { statusLabel: { _eq: 'Active' } });
        } else if (!isOnline && isOffline) {
            mergeNested('status', { statusLabel: { _eq: 'Inactive' } });
        }

        // Offline day ranges
        if (isOffline) {
            const now = new Date();
            const dateFilter: FilterOperator = {};

            if (map['offline-5-days']) {
                const fiveDaysAgo = new Date();
                fiveDaysAgo.setDate(now.getDate() - 5);
                dateFilter._gte = formatDateOnly(fiveDaysAgo);
                dateFilter._lte = formatDateOnly(now);
            } else if (map['offline-6-30-days']) {
                const thirtyDaysAgo = new Date();
                thirtyDaysAgo.setDate(now.getDate() - 30);
                const sixDaysAgo = new Date();
                sixDaysAgo.setDate(now.getDate() - 6);
                dateFilter._gte = formatDateOnly(thirtyDaysAgo);
                dateFilter._lte = formatDateOnly(sixDaysAgo);
            } else if (map['offline-30-plus-days']) {
                const thirtyOneDaysAgo = new Date();
                thirtyOneDaysAgo.setDate(now.getDate() - 31);
                dateFilter._lte = formatDateOnly(thirtyOneDaysAgo);
            }

            if (Object.keys(dateFilter).length > 0) {
                mergeNested('status', { updatedAt: dateFilter });
            }
        }

        // Assigned -> Pending
        if (map['pending']) {
            mergeNested('deviceStatus', {
                piStatus: { _eq: false },
                tvDisplayStatus: { _eq: false },
                displayStatus: { _eq: false },
                playerStatus: { _eq: false },
            });
        }

        // 3. Inactive
        if (map['inactive']) {
            mergeNested('deviceStatus', {
                piStatus: { _eq: true },
                tvDisplayStatus: { _eq: true },
                displayStatus: { _eq: true },
                playerStatus: { _eq: false },
            });
        }

        // 4. Recent Installs
        if (map['recent-installs']) {
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
            filters['installDate'] = {
                _gte: formatDateOnly(sevenDaysAgo),
            };
        }

        // 5. Order By (Radio buttons)
        if (map['order-online']) filters['statusId'] = { _eq: 1 };
        if (map['order-offline']) filters['statusId'] = { _eq: 0 };
        if (map['order-display-on']) mergeNested('deviceStatus', { displayStatus: { _eq: true } });
        if (map['order-display-off'])
            mergeNested('deviceStatus', { displayStatus: { _eq: false } });

        return filters;
    }

    /**
     * Fetches all available timezones from the {@link TimezoneService}.
     * Transforms the raw API response into a label-value format suitable for the dropdown.
     * Automatically unsubscribes when the component is destroyed using `takeUntilDestroyed`.
     * @private
     * @returns {void}
     */
    private getTimezones(): void {
        this.timezoneService
            .getAll()
            .pipe(
                withLoading(this.isLoading),
                catchError((error: unknown) => {
                    Logger.error('LicensesComponent', 'Error fetching timezones:', error);
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (response) => {
                    this.timezoneConfig.update((config) => ({
                        ...config,
                        options: mapTimezonesForDropdown(response.data),
                    }));
                },
            });
    }

    /**
     * Handles the timezone selection change.
     *
     * @param {DropdownOption | null} event - The selected option or null if the selection was cleared.
     * @returns {void}
     */
    public onTimezoneChange(event: DropdownOption | null): void {
        const value = event?.value;
        this.selectedTimezoneId.set(value !== undefined && value !== null ? Number(value) : null);
    }

    /**
     * Handles date range picker changes and triggers filtered API calls.
     * When no valid start date is provided, resets the filter and reloads all licenses.
     * When a valid range is provided, fetches licenses filtered by `installDate`.
     *
     * @param {{ start: Date; end: Date } | null} dates - The selected date range with `start` and `end` dates, or `null` to reset.
     * @returns {void}
     */
    public onDatesChange(dates: { start: Date; end: Date } | null): void {
        if (!dates?.start) {
            this.selectedDateRange.set(null);
            return;
        }

        const normalizedRange = {
            start: dates.start,
            end: dates.end ?? dates.start,
        };
        this.selectedDateRange.set(normalizedRange);
    }

    /**
     * Handles search input value changes and triggers a filtered data fetch.
     *
     * @param {string} term - The debounced search string from the search input.
     * @returns {void}
     */
    public onSearch(term: string): void {
        this.isLoading.set(true);
        this.isLoadMore = false;
        this.page.set(1);
        this.searchTerm.set(term);
    }

    /**
     * Handles generic action click events from the data table.
     * Triggers when the user clicks on an action button in the table toolbar.
     *
     * @param {{ action: string; row: UILicense }} event - The action event containing action name and row data.
     * @returns {void}
     */
    public onActionClick(event: { action: string; row: UILicense }): void {
        switch (event.action) {
            case 'screenshot':
                Logger.warn('LicensesComponent', 'Screenshot action not yet implemented');
                break;
            case 'delete':
                this.handleDeleteAction(event.row);
                break;
            default:
                Logger.warn('LicensesComponent', 'Unknown action:', event.action);
        }
    }

    /**
     * Handles cell click events from the table, specifically for columns with `hasRedirect: true`.
     * Opens the single license detailed page in a new window/tab.
     *
     * @param {{ field: string; value: string | number | undefined; row: UILicense }} event - The cell click event details.
     * @returns {void}
     */
    public onCellClick(event: {
        field: string;
        value: string | number | undefined;
        row: UILicense;
    }): void {
        if (event.row.id) {
            window.open(`/licenses/${event.row.id}`, '_blank');
        }
    }

    /**
     * Handles row selection change events from the table.
     * Updates the `selectedRows` signal to enable/disable toolbar actions.
     *
     * @param {UILicense[]} selectedRows - The array of currently selected rows.
     * @returns {void}
     */
    public onSelectionChange(selectedRows: UILicense[]): void {
        this.selectedRows.set(selectedRows);
    }

    /**
     * Handles global toolbar events (Bulk Delete, Export, etc.).
     *
     * @param {string} action - The unique identifier of the action clicked.
     * @returns {void}
     */
    public onToolbarAction(action: string): void {
        if (action === 'Bulk Delete') {
            const selectedIds = this.selectedRows()
                .map((row) => row.id)
                .filter(Boolean);
            if (selectedIds.length > 0) {
                this.showDeleteModal.set(true);
            }
        } else if (action === 'Export') {
            const dataToExport =
                this.selectedRows().length > 0 ? this.selectedRows() : this.licenseData();

            this.exportService.exportToExcel(dataToExport, 'Licenses', this.licenseColumns);
        }
    }

    /**
     * Opens the single license delete confirmation modal for the given row.
     *
     * @param {UILicense} row - The license row targeted for deletion.
     * @returns {void}
     */
    private handleDeleteAction(row: UILicense): void {
        this.licenseToDelete.set(row);
        this.showSingleDeleteModal.set(true);
    }

    /**
     * Closes the single license delete confirmation modal.
     *
     * @returns {void}
     */
    public closeSingleDeleteModal(): void {
        this.showSingleDeleteModal.set(false);
        this.licenseToDelete.set(null);
    }

    /**
     * Confirms and executes the deletion of a single license.
     *
     * @returns {void}
     */
    public onConfirmSingleDelete(): void {
        const license = this.licenseToDelete();
        if (!license?.id) return;

        this.licensesService
            .delete(license.id)
            .pipe(withLoading(this.isLoading), takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.closeSingleDeleteModal();
                    this.showSingleDeleteSuccessModal.set(true);
                    this.getAllLicenses();
                },
                error: (err: unknown) => {
                    Logger.error('LicensesComponent', 'Error deleting license:', err);
                },
            });
    }

    /**
     * Closes the bulk delete confirmation modal.
     *
     * @returns {void}
     */
    public closeDeleteModal(): void {
        this.showDeleteModal.set(false);
    }

    /**
     * Executes the bulk deletion of all currently selected licenses.
     * Refreshes the table and shows a success modal upon completion.
     *
     * @returns {void}
     */
    public onConfirmBulkDelete(): void {
        const selectedIds = this.selectedRows()
            .map((row) => row.id)
            .filter(Boolean);
        this.licensesService
            .bulkDelete(selectedIds)
            .pipe(withLoading(this.isLoading), takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.showDeleteModal.set(false);
                    this.showSuccessModal.set(true);
                    this.selectedRows.set([]);
                    this.getAllLicenses();
                },
                error: (err: unknown) => {
                    Logger.error('LicensesComponent', 'Error bulk deleting licenses:', err);
                },
            });
    }

    /**
     * Initializes the central reactive stream for fetching license data.
     *
     * This method orchestrates multiple signal-to-observable conversions to create a unified data stream.
     * It reacts to changes in:
     * - `searchTerm`: The current search query.
     * - `refreshTrigger`: Manual refresh increments-based trigger.
     * - `selectedTimezoneId`: The selected timezone filter ID.
     * - `filters$`: A computed composite filter state (date range, checkbox tree, etc.).
     *
     * The stream is piped through `takeUntilDestroyed` for automatic cleanup and updates the
     * `licenseData` and `isLoading` signals upon successful data retrieval.
     *
     * @returns {void}
     * @private
     */
    private initLicensesStream(): void {
        const searchTerm$ = toObservable(this.searchTerm);
        const refreshTrigger$ = toObservable(this.refreshTrigger);
        const timezoneId$ = toObservable(this.selectedTimezoneId);
        const page$ = toObservable(this.page);
        const limit$ = toObservable(this.limit);

        const filters$ = toObservable(
            computed((): QueryFilter => {
                const state: LicenseFilterState = {
                    dateRange: this.selectedDateRange(),
                    timezoneId: this.selectedTimezoneId(),
                    checkboxState: this.filterCheckedMap(),
                    refresh: this.refreshTrigger(),
                };
                return this.prepareFilters(state);
            }),
        );

        this.licensesService
            .getLicensesStream({
                searchTerm$,
                refreshTrigger$,
                filters$,
                timezoneId$,
                page$,
                limit$,
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => {
                    if (this.isLoadMore) {
                        this.licenseData.update((existing) => [...existing, ...response.data]);
                    } else {
                        this.licenseData.set(response.data);
                    }
                    this.isLoadMore = false;
                    this.isShowMoreLoading.set(false);
                    this.totalCount.set(response.total);
                    this.isLoading.set(false);
                },
                error: (err) => {
                    Logger.error('LicensesComponent', 'Fatal error in licenses stream:', err);
                    this.isLoadMore = false;
                    this.isShowMoreLoading.set(false);
                    this.isLoading.set(false);
                },
            });
    }

    public onShowMore(): void {
        if (this.licenseData().length < this.totalCount()) {
            this.isLoading.set(true);
            this.isLoadMore = true;
            this.isShowMoreLoading.set(true);
            this.page.update((p) => p + 1);
        }
    }

    /**
     * Collapses the accumulated license rows back to the first page.
     * Resets `page` so a subsequent "Show More" re-fetches page 2.
     */
    public onShowLess(): void {
        this.licenseData.update((current) => current.slice(0, this.limit()));
        this.page.set(1);
    }

    /**
     * Maps the current reactive UI state into a composite filter object for the data stream.
     *
     * @param {Object} filters - The source filter states.
     * @param {{ start: Date; end: Date } | null} filters.dateRange - The optional date range.
     * @param {number | null} filters.timezoneId - The optional timezone ID.
     * @param {Record<string, boolean>} filters.checkboxState - The map of active filter tree checkboxes.
     * @param {number} filters.refresh - The current refresh sequence number.
     * @returns {QueryFilter} A formatted filter object for the backend request.
     * @private
     */
    private prepareFilters(filters: {
        dateRange: { start: Date; end: Date } | null;
        timezoneId: number | null;
        checkboxState: Record<string, boolean>;
        refresh: number;
    }): QueryFilter {
        const apiFilters: QueryFilter = {};

        // 2. Date Range
        if (filters.dateRange) {
            apiFilters['installDate'] = {
                _gte: formatDateOnly(filters.dateRange.start),
                _lte: formatDateOnly(filters.dateRange.end),
            };
        }

        // 3. Checkbox filters from "Filter by" dropdown
        const checkboxFilters = this.buildFiltersFromCheckboxState();
        for (const [key, value] of Object.entries(checkboxFilters)) {
            if (apiFilters[key]) {
                apiFilters[key] = { ...apiFilters[key], ...value };
            } else {
                apiFilters[key] = value;
            }
        }

        return apiFilters;
    }

    /**
     * Triggers a manual refresh of the license data by updating the refresh signal.
     * Use this when data needs to be reloaded without filter changes (e.g., after deletion).
     *
     * @returns {void}
     */
    private getAllLicenses(): void {
        this.refreshTrigger.update((v) => v + 1);
    }

    /**
     * Fetches license statistics from the API and populates the stats dashboard cards.
     * Automatically unsubscribes when the component is destroyed via `takeUntilDestroyed`.
     */
    private getLicensesStats(): void {
        this.licensesService
            .getStats()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => {
                    this.statsData.set(this.mappedLicenseStats(response));
                    this.isStatsLoading.set(false);
                },
                error: (err) => {
                    Logger.error('LicensesComponent', 'Fatal error in licenses stats:', err);
                    this.isStatsLoading.set(false);
                },
            });
    }

    /**
     * Transforms raw `ApiLicenseStats` into the `StatsData[]` shape expected by the stats cards.
     * Produces three cards: total license counts broken down by type, Player UI version info,
     * and Player Server version info.
     *
     * @param stats - The raw stats object returned by the licenses API.
     * @returns An array of `StatsData` objects ready to be rendered by `app-stats-card`.
     */
    private mappedLicenseStats(stats: ApiLicenseStats): StatsData[] {
        return [
            {
                title: 'Total License(s)',
                description: 'Total number of licenses.',
                iconKey: 'dealers',
                total: stats.totalLicenses.overall.toString(),
                stats: [
                    {
                        label: 'Assigned',
                        value: (
                            Number(stats.totalLicenses.overall) -
                            Number(stats.totalUnassigned.overall)
                        ).toString(),
                        colorClass: 'text-orange-400',
                    },
                    {
                        label: 'Unassigned',
                        value: stats.totalUnassigned.overall.toString(),
                        colorClass: 'text-teal-500',
                    },
                ],
            },
            {
                title: 'Player UI v2.0.0',
                description: stats.playerUi.appDescription,
                iconKey: 'dealers',
                stats: [
                    {
                        label: 'Current Version',
                        value: stats.playerUi.currentVersion,
                        labelColor: '#8DCB2C',
                    },
                    {
                        label: 'Date Created',
                        value: formatDateLong(stats.playerUi.dateCreated),
                        labelColor: '#8DCB2C',
                    },
                    {
                        label: 'Time Posted',
                        value: stats.playerUi.dateCreated
                            ? formatTime(stats.playerUi.dateCreated)
                            : '--',
                        labelColor: '#8DCB2C',
                    },
                ],
            },
            {
                title: 'Player Servers',
                description: stats.playerServer.appDescription,
                iconKey: 'dealers',
                stats: [
                    {
                        label: 'Current Version',
                        value: stats.playerServer.currentVersion,
                        labelColor: '#8DCB2C',
                    },
                    {
                        label: 'Date Created',
                        value: formatDateLong(stats.playerServer.dateCreated),
                        labelColor: '#8DCB2C',
                    },
                    {
                        label: 'Time Posted',
                        value: stats.playerServer.dateCreated
                            ? formatTime(stats.playerServer.dateCreated)
                            : '--',
                        labelColor: '#8DCB2C',
                    },
                ],
            },
        ];
    }
}
